import React from 'react';

import Modal, { Props as ModalProps } from '@hero/ui-kit/components/Modal';
import Button from '@hero/ui-kit/inputs/Button';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import useMemberSendMonthlyCallReminder from '../../api/useMemberSendMonthlyCallReminder';
import useMemberSetRtmMemberDisengaged from '../../api/useMemberSetRtmDisengaged';
import * as Style from './style.module.scss';

type Props = {
    memberName: string;
    memberId: number;
    heroUserId: number;
    isCaregiver: boolean;
} & Pick<ModalProps, 'externalControls'>;

const MonthlyCallReminderModal: React.FC<Props> = ({
    externalControls,
    memberName,
    memberId,
    heroUserId,
    isCaregiver
}) => {
    const {
        isPending: isReminderPending,
        isError: isReminderError,
        mutate: setMonthlyCallReminder
    } = useMemberSendMonthlyCallReminder();

    const { isPending: isDisengagedPending, mutate: setRtmMemberDisengaged } =
        useMemberSetRtmMemberDisengaged();

    const [, setExternalState] = externalControls ?? [];

    const isPending = isReminderPending || isDisengagedPending;

    const isError = isReminderError;

    const handleClose = () => {
        setExternalState?.(false);
    };

    const handleSubmit = () => {
        setMonthlyCallReminder(
            { member_id: memberId },
            {
                onSuccess: () => {
                    if (heroUserId) {
                        setRtmMemberDisengaged(
                            { hero_user_id: heroUserId },
                            {
                                onSuccess: () => {
                                    handleClose();
                                }
                            }
                        );
                    } else {
                        handleClose();
                    }
                }
            }
        );
    };

    return (
        <Modal
            externalControls={externalControls}
            isCancelable
            styles={{ minWidth: '57rem', padding: 0 }}
        >
            {isError ? (
                <Section centered>
                    <H role="h5">Error</H>
                    <P>{memberName} cannot be reminded.</P>

                    <Button onClick={handleClose}>Got it</Button>
                </Section>
            ) : (
                <Section centered>
                    <H role="h5">Send monthly call reminder</H>
                    <P>
                        {memberName} will receive a reminder across their connected channels
                        <br /> (SMS, Email, App, and Dispenser):
                    </P>
                    <div className={Style.preview}>
                        <P strong>📣 Important Reminder</P>
                        {isCaregiver ? (
                            <P>
                                To stay enrolled in your provider&apos;s remote care program,{' '}
                                {memberName} must attend their monthly check-in call.
                                <br />
                                Missing this call puts their Hero subscription at risk.
                                <br />
                                Call provider NOW to avoid any interruptions in {memberName}'s care
                                and access to the Hero platform.
                            </P>
                        ) : (
                            <P>
                                To stay enrolled in your provider&apos;s remote care program, you
                                must attend your monthly check-in call.
                                <br />
                                Missing this call puts your Hero subscription at risk.
                                <br />
                                Call provider NOW to avoid any interruptions in your care and access
                                to the Hero platform.
                            </P>
                        )}
                    </div>
                    <Button disabled={isPending} onClick={handleSubmit}>
                        Confirm
                    </Button>
                </Section>
            )}
        </Modal>
    );
};

export default MonthlyCallReminderModal;
